import { useState, useMemo, useCallback, useEffect } from 'react';
import {
    useGetMainCategoriesQuery,
    useGetSecondCategoriesQuery,
    useGetSubCategoriesQuery,
    useGetSubPartCategoriesQuery,
    useGetFailureModesCategoryQuery
} from '../../../../api';
import { CATEGORY_LEVELS } from '../components/CategoryTable';
import { useLanguage } from '../../../../contexts/LanguageContext';

// Kategori listelerini getirmek ve yönetmek için kullanılan custom hook.
// Filtreleme, sayfalama, sıralama ve dil seçeneklerini destekler.
export const useCategoryList = (initialHookParams = {}) => {
    const { getCurrentLanguageId } = useLanguage();

    const {
        level: initialLevel = CATEGORY_LEVELS.MAIN,
        mainCategory: initialMainCategory = null,
        secondaryCategory: initialSecondaryCategory = null,
        subCategory: initialSubCategory = null,
        searchText: initialSearchText = '',
        page: initialPage = 1,
        rows: initialRows = 10,
        filters: initialFilters = {},
        sortField: initialSortField = null,
        sortOrder: initialSortOrder = null,
    } = initialHookParams;

    const [lazyParams, setLazyParams] = useState({
        page: initialPage,
        rows: initialRows,
        searchText: initialSearchText,
        level: initialLevel,
        mainCategory: initialMainCategory,
        secondaryCategory: initialSecondaryCategory,
        subCategory: initialSubCategory,
        filters: initialFilters,
        sortField: initialSortField,
        sortOrder: initialSortOrder,
    });

    // `initialHookParams` değiştiğinde `lazyParams`'ı günceller.
    // Kategori seviyesi veya üst kategoriler değişirse sayfayı sıfırlar.
    useEffect(() => {
        setLazyParams(prev => {
            const shouldResetPage = initialHookParams.level !== prev.level ||
                initialHookParams.mainCategory !== prev.mainCategory ||
                initialHookParams.secondaryCategory !== prev.secondaryCategory ||
                initialHookParams.subCategory !== prev.subCategory ||
                JSON.stringify(initialHookParams.filters) !== JSON.stringify(prev.filters);
            const newPage = shouldResetPage ? 1 : (initialHookParams.page !== undefined ? initialHookParams.page : prev.page);

            return {
                ...prev,
                level: initialHookParams.level !== undefined ? initialHookParams.level : prev.level,
                mainCategory: initialHookParams.mainCategory !== undefined ? initialHookParams.mainCategory : prev.mainCategory,
                secondaryCategory: initialHookParams.secondaryCategory !== undefined ? initialHookParams.secondaryCategory : prev.secondaryCategory,
                subCategory: initialHookParams.subCategory !== undefined ? initialHookParams.subCategory : prev.subCategory,
                searchText: initialHookParams.searchText !== undefined ? initialHookParams.searchText : prev.searchText,
                languageId: initialHookParams.languageId !== undefined ? initialHookParams.languageId : prev.languageId,
                rows: initialHookParams.rows !== undefined ? initialHookParams.rows : prev.rows,
                page: newPage,
                filters: initialHookParams.filters !== undefined ? initialHookParams.filters : prev.filters,
                sortField: initialHookParams.sortField !== undefined ? initialHookParams.sortField : prev.sortField,
                sortOrder: initialHookParams.sortOrder !== undefined ? initialHookParams.sortOrder : prev.sortOrder,
            };
        });
    }, [
        initialHookParams.level,
        initialHookParams.mainCategory,
        initialHookParams.secondaryCategory,
        initialHookParams.subCategory,
        initialHookParams.searchText,
        initialHookParams.languageId,
        initialHookParams.page,
        initialHookParams.rows,
        initialHookParams.filters,
        initialHookParams.sortField,
        initialHookParams.sortOrder,
    ]);

    // API sorgusu için `lazyParams`'a göre parametreleri hazırlar.
    const queryApiParams = useMemo(() => {
        const baseParams = {
            LanguageId: getCurrentLanguageId(), // Context'den otomatik languageId
            SearchText: lazyParams.searchText,
            PageNumber: 1, // Frontend sıralama için tüm veriyi çek
            PageSize: 1000, // Yeterince büyük bir sayı
        };

        // Add filter parameters if they exist
        if (lazyParams.filters) {
            if (lazyParams.filters.isActive !== null && lazyParams.filters.isActive !== undefined) {
                baseParams.IsActive = lazyParams.filters.isActive;
            }

            // Add hierarchical filter parameters based on level
            if (lazyParams.filters.mainCategory) {
                baseParams.mainCategory = lazyParams.filters.mainCategory;
            }
            if (lazyParams.filters.secondaryCategory) {
                baseParams.secondaryCategory = lazyParams.filters.secondaryCategory;
            }
            if (lazyParams.filters.subCategory) {
                baseParams.subCategory = lazyParams.filters.subCategory;
            }
            if (lazyParams.filters.subPartCategory) {
                baseParams.subPartCategory = lazyParams.filters.subPartCategory;
            }
        }

        let specificParams = { ...baseParams };

        switch (lazyParams.level) {
            case CATEGORY_LEVELS.SECONDARY:
                // Always include mainCategory from either context or filters
                specificParams.mainCategory = lazyParams.mainCategory || (lazyParams.filters?.mainCategory) || null;
                break;
            case CATEGORY_LEVELS.SUB:
                // Always include mainCategory and secondaryCategory
                specificParams.mainCategory = lazyParams.mainCategory || (lazyParams.filters?.mainCategory) || null;
                specificParams.secondaryCategory = lazyParams.secondaryCategory || (lazyParams.filters?.secondaryCategory) || null;
                break;
            case CATEGORY_LEVELS.SUB_PART:
                // Always include mainCategory, secondaryCategory, and subCategory
                specificParams.mainCategory = lazyParams.mainCategory || (lazyParams.filters?.mainCategory) || null;
                specificParams.secondaryCategory = lazyParams.secondaryCategory || (lazyParams.filters?.secondaryCategory) || null;
                specificParams.subCategory = lazyParams.subCategory || (lazyParams.filters?.subCategory) || null;
                break;
            case CATEGORY_LEVELS.MAIN:
            case CATEGORY_LEVELS.FAILURE_MODES:
            default:
                break;
        }
        return specificParams;
    }, [lazyParams, getCurrentLanguageId]); // getCurrentLanguageId dependency eklendi

    // Mevcut kategori seviyesine göre uygun API sorgu hook'unu seçer.
    let queryHook;
    switch (lazyParams.level) {
        case CATEGORY_LEVELS.MAIN: queryHook = useGetMainCategoriesQuery; break;
        case CATEGORY_LEVELS.SECONDARY: queryHook = useGetSecondCategoriesQuery; break;
        case CATEGORY_LEVELS.SUB: queryHook = useGetSubCategoriesQuery; break;
        case CATEGORY_LEVELS.SUB_PART: queryHook = useGetSubPartCategoriesQuery; break;
        case CATEGORY_LEVELS.FAILURE_MODES: queryHook = useGetFailureModesCategoryQuery; break;
        default: queryHook = useGetMainCategoriesQuery;
    }

    const skipQuery = useMemo(() => {
        // Belirli durumlarda sorguyu atlamak için buraya mantık eklenebilir.
        // Örneğin, gerekli bir üst kategori ID'si yoksa.
        // Mevcut durumda, her seviye bağımsız olarak listelenebildiği için false dönülüyor.
        return false;
    }, []);

    const {
        data: apiResponse,
        isLoading,
        isFetching,
        error,
        refetch
    } = queryHook(queryApiParams, { skip: skipQuery });

    // Helper function to get text from translations
    const getTranslationText = useCallback((translations, languageId) => {
        if (!translations || !Array.isArray(translations)) return '';
        const translation = translations.find(t => t.languageId === languageId);
        return translation?.text || '';
    }, []);

    // Frontend sıralama fonksiyonu
    const sortData = useCallback((data) => {
        if (!lazyParams.sortField || !data || data.length === 0) return data;

        const sortedData = [...data].sort((a, b) => {
            let aValue, bValue;

            // Sıralama alanına göre değerleri al
            switch (lazyParams.sortField) {
                case 'turkishName':
                    aValue = getTranslationText(a.translations, 1); // Turkish language ID = 1
                    bValue = getTranslationText(b.translations, 1);
                    break;
                case 'englishName':
                    aValue = getTranslationText(a.translations, 2); // English language ID = 2
                    bValue = getTranslationText(b.translations, 2);
                    break;
                case 'mainCategoryName':
                    aValue = a.mainCategoryName || '';
                    bValue = b.mainCategoryName || '';
                    // Ana kategori sütununa tıklandıysa sadece ana kategoriye göre sırala
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return lazyParams.sortOrder === 1
                            ? aValue.localeCompare(bValue, 'tr-TR')
                            : bValue.localeCompare(aValue, 'tr-TR');
                    }
                    break;
                case 'secondaryCategoryName': {
                    aValue = a.secondaryCategoryName || '';
                    bValue = b.secondaryCategoryName || '';
                    // İkincil kategori sütununa tıklandıysa önce ana, sonra ikincil kategoriye göre sırala
                    const aMain1 = a.mainCategoryName || '';
                    const bMain1 = b.mainCategoryName || '';
                    if (aMain1 !== bMain1) {
                        return aMain1.localeCompare(bMain1, 'tr-TR');
                    }
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return lazyParams.sortOrder === 1
                            ? aValue.localeCompare(bValue, 'tr-TR')
                            : bValue.localeCompare(aValue, 'tr-TR');
                    }
                    break;
                }
                case 'subCategoryName': {
                    aValue = a.subCategoryName || '';
                    bValue = b.subCategoryName || '';
                    // Alt kategori sütununa tıklandıysa önce ana, sonra ikincil, sonra alt kategoriye göre sırala
                    const aMain2 = a.mainCategoryName || '';
                    const bMain2 = b.mainCategoryName || '';
                    if (aMain2 !== bMain2) {
                        return aMain2.localeCompare(bMain2, 'tr-TR');
                    }
                    const aSecond = a.secondaryCategoryName || '';
                    const bSecond = b.secondaryCategoryName || '';
                    if (aSecond !== bSecond) {
                        return aSecond.localeCompare(bSecond, 'tr-TR');
                    }
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return lazyParams.sortOrder === 1
                            ? aValue.localeCompare(bValue, 'tr-TR')
                            : bValue.localeCompare(aValue, 'tr-TR');
                    }
                    break;
                }
                case 'isActive':
                    aValue = a.isActive;
                    bValue = b.isActive;
                    if (typeof aValue === 'number' && typeof bValue === 'number') {
                        return lazyParams.sortOrder === 1
                            ? aValue - bValue
                            : bValue - aValue;
                    }
                    break;
                default:
                    aValue = a[lazyParams.sortField] || '';
                    bValue = b[lazyParams.sortField] || '';
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return lazyParams.sortOrder === 1
                            ? aValue.localeCompare(bValue, 'tr-TR')
                            : bValue.localeCompare(aValue, 'tr-TR');
                    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                        return lazyParams.sortOrder === 1
                            ? aValue - bValue
                            : bValue - aValue;
                    }
                    break;
            }

            // Eğer yukarıdaki switch case'lerden değer döndürülmediyse
            // Türkçe ve İngilizce isimler için hiyerarşik sıralama yap
            if (lazyParams.sortField === 'turkishName' || lazyParams.sortField === 'englishName') {
                // Önce hiyerarşik sıralama
                if (lazyParams.level !== CATEGORY_LEVELS.MAIN) {
                    // Ana kategori karşılaştırması
                    const aMain = a.mainCategoryName || '';
                    const bMain = b.mainCategoryName || '';
                    if (aMain !== bMain) {
                        return aMain.localeCompare(bMain, 'tr-TR');
                    }

                    // İkincil kategori karşılaştırması (eğer varsa)
                    if (lazyParams.level === CATEGORY_LEVELS.SUB || lazyParams.level === CATEGORY_LEVELS.SUB_PART) {
                        const aSecond = a.secondaryCategoryName || '';
                        const bSecond = b.secondaryCategoryName || '';
                        if (aSecond !== bSecond) {
                            return aSecond.localeCompare(bSecond, 'tr-TR');
                        }
                    }

                    // Alt kategori karşılaştırması (eğer varsa)
                    if (lazyParams.level === CATEGORY_LEVELS.SUB_PART) {
                        const aSub = a.subCategoryName || '';
                        const bSub = b.subCategoryName || '';
                        if (aSub !== bSub) {
                            return aSub.localeCompare(bSub, 'tr-TR');
                        }
                    }
                }

                // Son olarak istenilen alan için karşılaştırma yap
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return lazyParams.sortOrder === 1
                        ? aValue.localeCompare(bValue, 'tr-TR')
                        : bValue.localeCompare(aValue, 'tr-TR');
                }
            }

            // Varsayılan dönüş değeri
            return 0;
        });

        return sortedData;
    }, [lazyParams.sortField, lazyParams.sortOrder, lazyParams.level, getTranslationText]);

    // API yanıtından gelen kategori listesini sırala ve sayfalandır
    const { items, totalRecords } = useMemo(() => {
        if (skipQuery || !apiResponse?.data) {
            return { items: [], totalRecords: 0 };
        }

        // Önce sırala
        const sortedData = sortData(apiResponse.data);

        // Sonra sayfalandır
        const startIndex = (lazyParams.page - 1) * lazyParams.rows;
        const endIndex = startIndex + lazyParams.rows;
        const paginatedData = sortedData.slice(startIndex, endIndex);

        return {
            items: paginatedData,
            totalRecords: sortedData.length
        };
    }, [apiResponse, skipQuery, sortData, lazyParams.page, lazyParams.rows]);

    // Sayfa veya satır sayısı değiştiğinde `lazyParams`'ı günceller.
    const onPageChange = useCallback((event) => {
        setLazyParams(prev => ({ ...prev, page: event.page + 1, rows: event.rows }));
    }, []);

    // Arama metni değiştiğinde `lazyParams`'ı günceller ve sayfayı sıfırlar.
    const onFilterChange = useCallback((newSearchText) => {
        setLazyParams(prev => ({ ...prev, searchText: newSearchText, page: 1 }));
    }, []);

    // Dil seçimi değiştiğinde `lazyParams`'ı günceller ve sayfayı sıfırlar.
    const onLanguageChange = useCallback((newLanguageId) => {
        setLazyParams(prev => ({ ...prev, languageId: newLanguageId, page: 1 }));
    }, []);

    // Kategori seviyesini, üst kategori ID'lerini, arama metnini, filtreleri ve sıralamayı ayarlar, sayfayı sıfırlar.
    const setContext = useCallback((newLevel, newParentIdsOrFilters = {}, newSearchText = '', sortingParams = {}) => {
        setLazyParams(prev => {
            // If newParentIdsOrFilters contains filter properties, use them as filters
            const hasFilterProperties = 'isActive' in newParentIdsOrFilters ||
                'mainCategory' in newParentIdsOrFilters ||
                'secondaryCategory' in newParentIdsOrFilters ||
                'subCategory' in newParentIdsOrFilters ||
                'subPartCategory' in newParentIdsOrFilters;

            if (hasFilterProperties) {
                // newParentIdsOrFilters is actually filters
                return {
                    ...prev,
                    level: newLevel,
                    filters: newParentIdsOrFilters,
                    page: 1,
                    searchText: newSearchText,
                    sortField: sortingParams.sortField !== undefined ? sortingParams.sortField : prev.sortField,
                    sortOrder: sortingParams.sortOrder !== undefined ? sortingParams.sortOrder : prev.sortOrder,
                };
            } else {
                // newParentIdsOrFilters is parent IDs (backward compatibility)
                return {
                    ...prev,
                    level: newLevel,
                    mainCategory: newParentIdsOrFilters.mainCategory !== undefined ? newParentIdsOrFilters.mainCategory : null,
                    secondaryCategory: newParentIdsOrFilters.secondaryCategory !== undefined ? newParentIdsOrFilters.secondaryCategory : null,
                    subCategory: newParentIdsOrFilters.subCategory !== undefined ? newParentIdsOrFilters.subCategory : null,
                    page: 1,
                    searchText: newSearchText,
                    sortField: sortingParams.sortField !== undefined ? sortingParams.sortField : prev.sortField,
                    sortOrder: sortingParams.sortOrder !== undefined ? sortingParams.sortOrder : prev.sortOrder,
                };
            }
        });
    }, []);

    return {
        items,
        totalRecords,
        isLoading: isLoading || isFetching,
        isFetching,
        error,
        refetch,
        lazyParams,
        onPageChange,
        onFilterChange,
        onLanguageChange,
        setContext,
        currentLevel: lazyParams.level,
        skipQuery,
    };
};